import {
  ArrowDown,
  ArrowUp,
  CreditCard,
  Money,
  Upload,
  Wallet,
} from '@factorialco/f0-react/icons/app'
import { BankingTransactionFieldsFragment } from 'generated/resources/graphql'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import { dateBackendDateTimeToUI } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { formattedAmount } from 'modules/banking/lib/paymentHelpers'

interface Props {
  locale: string
  bankAccountId: string
}

export const useAccountTransactionsDataCollection = ({ locale, bankAccountId }: Props) => {
  const { navigation } = useNavigation()

  const getTypeTag = (transaction: BankingTransactionFieldsFragment) => {
    const { type, cardPayment } = transaction
    const last4 = cardPayment?.card?.last4

    switch (type) {
      case 'topup':
        return {
          label: i18n.t('banking.accounts.transaction_type.topup'),
          icon: ArrowDown,
        }
      case 'payroll':
        return {
          label: i18n.t('banking.accounts.transaction_type.payroll'),
          icon: Money,
        }
      case 'card_payment':
        return {
          label: !last4
            ? i18n.t('banking.accounts.transaction_type.card_payment')
            : i18n.t('banking.transactions.types.card_payment', { cardLast4numbers: last4 }),
          icon: CreditCard,
        }
      case 'dispute':
        return {
          label: i18n.t('banking.accounts.transaction_type.dispute'),
          icon: CreditCard,
        }
      case 'incoming':
        return {
          label: i18n.t('banking.accounts.transaction_type.incoming'),
          icon: ArrowDown,
        }
      case 'fees':
        return {
          label: i18n.t('banking.accounts.transaction_type.fee'),
          icon: Wallet,
        }
      case 'card_refund':
        return {
          label: i18n.t('banking.accounts.transaction_type.card_refund'),
          icon: CreditCard,
        }
      case 'outgoing':
        return {
          label: i18n.t('banking.accounts.transaction_type.outgoing'),
          icon: ArrowUp,
        }
      case 'incoming_transfer':
        return {
          label: i18n.t('banking.accounts.transaction_type.incoming_transfer'),
          icon: Upload,
        }
      default:
        return {
          label: i18n.t('banking.accounts.transaction_type.unknown'),
          icon: Money,
        }
    }
  }

  return {
    sortings: {
      bookingDate: {
        label: i18n.t('banking.attributes.booking_date'),
      },
    },
    defaultSortings: {
      field: 'bookingDate' as const,
      order: 'desc' as const,
    },
    columns: [
      {
        label: i18n.t('banking.attributes.booking_date'),
        sorting: 'bookingDate' as const,
        width: 110,
        render: (transaction: BankingTransactionFieldsFragment) => ({
          type: 'date' as const,
          value: dateBackendDateTimeToUI(transaction.bookingDate),
        }),
      },
      {
        label: i18n.t('banking.attributes.type'),
        width: 220,
        render: (transaction: BankingTransactionFieldsFragment) => ({
          type: 'tag' as const,
          value: getTypeTag(transaction),
        }),
      },
      {
        label: i18n.t('banking.attributes.description'),
        render: (transaction: BankingTransactionFieldsFragment) => ({
          type: 'text' as const,
          value: transaction.description || '',
        }),
      },
      {
        label: i18n.t('banking.attributes.category'),
        render: (transaction: BankingTransactionFieldsFragment) => ({
          type: 'tag' as const,
          value: {
            label: transaction.category?.label ?? i18n.t('banking.attributes.uncategorized'),
          },
        }),
      },
      {
        label: i18n.t('banking.attributes.amount'),
        width: 150,
        render: (transaction: BankingTransactionFieldsFragment) => ({
          type: 'text' as const,
          value: formattedAmount({
            amountCents: transaction.amountCents,
            currency: transaction.currency,
            locale,
          }),
        }),
      },
      {
        label: i18n.t('banking.attributes.balance'),
        width: 150,
        render: (transaction: BankingTransactionFieldsFragment) => ({
          type: 'text' as const,
          value: formattedAmount({
            amountCents: Number(transaction.balanceAfterCents),
            currency: transaction.currency,
            locale,
          }),
        }),
      },
    ],
  }
}
